import WfLoading from './components/wf-loading';
import WfForm, {
  convertCascader,
  convertInput,
  convertSelect,
  convertTextarea,
  WfFormItem,
  convertCheckbox,
  convertCheckboxGroup,
  convertDayPicker,
} from './components/wf-form';
import WfSelect from './components/wf-select';
import WfCascader from './components/wf-cascader';
import WfCheckbox from './components/wf-checkbox';
import WfInput from './components/wf-input';
import WfTextarea from './components/wf-textarea';
import WfDatePicker from './components/wf-date-picker';
import WfUpload from './components/wf-upload';
import WfCaptcha from './components/wf-captcha';
import WfSubmitBtn from './components/wf-submit-btn';
import WfPageTip from './components/wf-page-tip';
import { NetworkErrorSvg, RefreshSvg, UnavailableErrorSvg } from './components/wf-svg';

import useWebForm from './hooks/useWebForm';

import { CaseFormModel } from '@/models/case-form.model';
import { FieldWithOptionType } from '@/models/case-field.model';
import { FieldComponentTypeEnum } from '@/models/component-type.model';
import { WebformLanguage } from '@/models/web-form.model';

import { genRules, maximums } from './utils';

import './variables.scss';
import './index.scss';

enum SubmitStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

export interface WebFormParams {
  isPreview?: boolean;
  formList?: CaseFormModel[];
  withOptions?: FieldWithOptionType[];
  disableAttachment?: boolean;
  disableCaptcha?: boolean;
  language?: WebformLanguage;
  i18nResource?: Record<string, string>;
  showMessageAtBottom?: boolean;
}

function WebForm(props: WebFormParams) {
  const { isPreview } = props;

  const {
    formRef,
    curSubmitStatus,
    allFormList,
    curForm,
    disableAttachment,
    disableCaptcha,
    language,
    showMessageAtBottom,
    captchaImgSrc,
    uploadedFiles,
    setUploadedFiles,
    captchaLoading,
    showWebFormPage,
    showLoading,
    showSubmitSuccessPage,
    showNetworkErrorPage,
    showUnavailablePage,
    curWithOptions,
    formOptions,
    t,
    handleRefresh,
    handleRefreshCaptcha,
    handleChangeForm,
    handleSubmit,
  } = useWebForm(props);

  return (
    <div id="wfContainer">
      <div style={{ display: showWebFormPage ? 'block' : 'none' }} className="wf-container">
        <WfForm ref={formRef}>
          {/* Form Field */}
          {allFormList.length === 1 ? (
            <p className="wf-unique-form-name">{curForm?.shown_contact}</p>
          ) : (
            // Please choose your issue
            <WfFormItem
              key="form_id"
              id="form_id"
              label={t('webform.form.placeholder')}
              name="form_id"
              defaultValue={curForm?.id}
              required
              t={t}
              render={(props) => (
                <WfSelect
                  {...convertSelect(props)}
                  options={formOptions}
                  placeholder={t('webform.form.placeholder')}
                  allowClear
                  t={t}
                  onChange={(value, option) => {
                    props.field.onChange(value);
                    handleChangeForm(value, option);
                  }}
                />
              )}
            />
          )}

          {/* Other fields */}
          {curWithOptions?.map((item) => {
            const curField = curForm?.field_lists.find(({ key }) => key === item.key);
            const isEmail = item.key === 'email';

            const label = curField?.contact_display_name || item.label;
            const placeholder = isEmail ? t('webform.email.placeholder') : curField?.shown_contact_description;
            const required = isEmail || curField?.contact_required_submit || false;
            const maxLength = maximums[item.component_type];

            return (
              <WfFormItem
                key={item.key}
                id={item.id}
                label={item.component_type !== FieldComponentTypeEnum.Checkbox ? label : ''}
                name={item.key}
                required={required}
                rules={genRules(item, required, t)}
                t={t}
                render={(props) => (
                  <>
                    {item.component_type === FieldComponentTypeEnum.Dropdown && (
                      <WfSelect
                        {...convertSelect(props)}
                        options={item.options}
                        placeholder={placeholder}
                        allowClear
                        t={t}
                      />
                    )}

                    {item.component_type === FieldComponentTypeEnum.DependentField && (
                      <WfCascader
                        {...convertCascader(props)}
                        options={JSON.parse((Array.isArray(item.options) && item.options[0]?.label) || '[]')}
                        allowClear
                        t={t}
                      />
                    )}

                    {item.component_type === FieldComponentTypeEnum.MultiSelect && (
                      <WfCheckbox.Group {...convertCheckboxGroup(props)} options={item.options} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.Text && (
                      <WfInput {...convertInput(props)} placeholder={placeholder} maxLength={maxLength} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.MultiLineText && (
                      <WfTextarea {...convertTextarea(props)} placeholder={placeholder} maxLength={maxLength} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.Numeric && (
                      <WfInput {...convertInput(props)} placeholder={placeholder} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.Decimal && (
                      <WfInput {...convertInput(props)} placeholder={placeholder} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.Checkbox && (
                      <WfCheckbox {...convertCheckbox(props)} label={item.label} required={required} />
                    )}

                    {item.component_type === FieldComponentTypeEnum.Date && (
                      <>
                        <WfDatePicker
                          {...convertDayPicker(props)}
                          placeholder={placeholder}
                          language={language}
                          value={props.field.value?.toLocaleDateString()}
                        />
                      </>
                    )}
                  </>
                )}
              />
            );
          })}

          {curForm?.id && (
            <>
              {!disableAttachment && (
                <WfUpload
                  isPreview={isPreview}
                  uploadedFiles={uploadedFiles}
                  t={t}
                  setUploadedFiles={setUploadedFiles}
                />
              )}

              {!disableCaptcha && (
                <WfCaptcha
                  imgSrc={captchaImgSrc}
                  isPreview={isPreview}
                  loading={captchaLoading}
                  t={t}
                  onRefresh={handleRefreshCaptcha}
                />
              )}
            </>
          )}
        </WfForm>

        {curForm?.id && (
          <WfSubmitBtn
            disabled={uploadedFiles.some((file) => file.status === 'Loading')}
            loading={curSubmitStatus === SubmitStatusEnum.LOADING}
            error={curSubmitStatus === SubmitStatusEnum.ERROR}
            t={t}
            onClick={handleSubmit}
          />
        )}
      </div>

      {showLoading && <WfLoading size="large" />}

      {showSubmitSuccessPage && (
        <WfPageTip title={t('webform.submit_request.title.success')} showMessageAtBottom={showMessageAtBottom} />
      )}

      {showNetworkErrorPage && (
        <WfPageTip
          SvgComponent={NetworkErrorSvg}
          title="Something went wrong. Please try again later."
          BtnSvgComponent={RefreshSvg}
          btnText="REFRESH"
          onClickBtn={handleRefresh}
        />
      )}

      {showUnavailablePage && <WfPageTip SvgComponent={UnavailableErrorSvg} title="Something went wrong." />}
    </div>
  );
}

export default WebForm;
